import { createContext, useState, useEffect, useContext, useCallback, useRef } from "react";

const StatusContext = createContext();

const STATUSPAGE_BASE = "https://status.eng.umd.edu/api/v2";
const PINPOINT_COMPONENT_ID = "15jsmf82gtjt";
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const FETCH_TIMEOUT_MS = 10 * 1000; // 10 seconds

async function fetchWithTimeout(url, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export function StatusProvider({ children }) {
  const [status, setStatus] = useState("operational");
  const [incidents, setIncidents] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    const [componentsData, incidentsData] = await Promise.all([
      fetchWithTimeout(`${STATUSPAGE_BASE}/components.json`, FETCH_TIMEOUT_MS),
      fetchWithTimeout(`${STATUSPAGE_BASE}/incidents/unresolved.json`, FETCH_TIMEOUT_MS),
    ]);

    // Extract Pinpoint component status
    if (componentsData && Array.isArray(componentsData.components)) {
      const pinpoint = componentsData.components.find(
        (c) => c.id === PINPOINT_COMPONENT_ID
      );
      if (pinpoint) {
        setStatus(pinpoint.status);
      }
    }

    // Filter incidents affecting Pinpoint
    if (incidentsData && Array.isArray(incidentsData.incidents)) {
      const pinpointIncidents = incidentsData.incidents.filter((incident) =>
        Array.isArray(incident.components) &&
        incident.components.some((c) => c.id === PINPOINT_COMPONENT_ID)
      );
      setIncidents(pinpointIncidents);
    }

    setLastUpdated(Date.now());
  }, []);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [fetchStatus]);

  return (
    <StatusContext.Provider value={{ status, incidents, lastUpdated }}>
      {children}
    </StatusContext.Provider>
  );
}

export function useStatus() {
  return useContext(StatusContext);
}
